/**
 * Legal Communications Workspace
 * 3-panel: Left nav · Thread list · Active thread + context
 * Professional outbound email with chambers-branded signatures.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useServerSearch } from '@/lib/useServerSearch';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChatTeardropDots, Briefcase, ArrowSquareOut,
  PaperPlaneTilt, MagnifyingGlass, CaretRight, CaretLeft,
  Paperclip, Buildings,
  CircleNotch, Info, PencilSimple, GlobeHemisphereWest,
} from '@phosphor-icons/react';
import { useAuthStore } from '@/features/auth/store';
import { useMattersStore } from '@/lib/mattersStore';
import { useHearingsStore } from '@/lib/hearingsStore';
import {
  fetchThreads, fetchThread, fetchMessages, sendMessage, markThreadRead,
  type MessageThread, type ChatMessage, type ThreadKind,
} from '@/lib/messagingApi';
import { NewThreadModal } from '@/components/messaging/NewThreadModal';
import { CrossChambersModal } from '@/components/messaging/CrossChambersModal';
import { fetchDocuments } from '@/lib/documentsApi';
import { CommunicationsTabBar } from '@/components/shared/CommunicationsTabBar';
import { cn } from '@/lib/utils';
import { BackButton } from '@/components/shared/BackButton';

// ─── Helpers ──────────────────────────────────────────────────

function timeLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 60_000)     return 'now';
  if (diff < 3_600_000)  return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (diff < 7 * 86_400_000) return d.toLocaleDateString('en-GB', { weekday: 'short' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

const PRIVILEGE_LABELS: Record<string, { label: string; color: string }> = {
  legal_advice:       { label: 'LP', color: 'bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300' },
  without_prejudice:  { label: 'WP', color: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  none:               { label: '', color: '' },
};

const KIND_ICONS: Record<string, React.ReactNode> = {
  matter_internal:  <Briefcase size={11} />,
  matter_external:  <ArrowSquareOut size={11} />,
  chambers_internal: <Buildings size={11} />,
  cross_chambers:   <GlobeHemisphereWest size={11} />,
};

/** Title for a thread row/header. Cross-chambers & subject-less threads show the other party's name. */
function threadTitle(thread: MessageThread, currentUserId?: string): string {
  if (thread.subject) return thread.subject;
  if (thread.case) return `${thread.case.caseNumber} ${thread.case.caseName}`;
  const others = (thread.participants ?? [])
    .filter((p) => p.userId !== currentUserId)
    .map((p) => `${p.user.firstName} ${p.user.lastName}`);
  if (others.length === 0) return 'New conversation';
  return others.length === 1 ? others[0]! : `${others[0]} + ${others.length - 1} more`;
}

// ─── Thread row ───────────────────────────────────────────────

function ThreadRow({ thread, isActive, onClick, currentUserId }: {
  thread: MessageThread; isActive: boolean; onClick: () => void; currentUserId?: string;
}) {
  const privInfo = PRIVILEGE_LABELS[thread.privilege ?? 'none'] ?? PRIVILEGE_LABELS['none']!;
  const unread = (thread.unreadCount ?? 0) > 0;
  const lastPreview = thread.messages?.[thread.messages.length - 1]?.body ?? '';
  const caseNum = thread.case?.caseNumber ?? null;
  const isCross = thread.kind === 'cross_chambers';

  return (
    <button onClick={onClick}
      className={cn(
        'w-full text-left px-4 py-3 border-b border-border/30 transition-colors hover:bg-muted/30',
        isActive && 'bg-brand-50/60 border-l-2 border-l-brand-600',
        !isActive && 'border-l-2 border-l-transparent',
      )}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className={cn('text-xs truncate', unread ? 'font-semibold text-foreground' : 'font-medium text-foreground/80')}>
              {threadTitle(thread, currentUserId)}
            </span>
            {isCross && (
              <span className="shrink-0 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[8px] font-bold uppercase tracking-wide bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300">
                <GlobeHemisphereWest size={8} weight="fill" />External
              </span>
            )}
            {privInfo.label && (
              <span className={cn('shrink-0 rounded px-1 py-0.5 text-[8px] font-bold', privInfo.color)}>{privInfo.label}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="shrink-0 opacity-60">{KIND_ICONS[thread.kind]}</span>
            {caseNum && (
              <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[9px] font-medium text-brand-700 dark:text-brand-300 shrink-0">
                {caseNum}
              </span>
            )}
            <span className="truncate">{lastPreview || 'No messages yet'}</span>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="text-[10px] text-muted-foreground/60">{timeLabel(thread.lastMessageAt ?? thread.updatedAt)}</span>
          {unread && (
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-brand-600 text-[9px] font-bold text-white">
              {thread.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ─── Message bubble ───────────────────────────────────────────

function MessageBubble({ msg, isOwn }: { msg: ChatMessage; isOwn: boolean }) {
  const TYPE_STYLES: Record<string, string> = {
    urgent:       'border-l-2 border-red-400',
    instruction:  'border-l-2 border-blue-400',
    hearing_update: 'border-l-2 border-amber-400',
    fee_discussion: 'border-l-2 border-emerald-400',
  };
  const typeStyle = TYPE_STYLES[msg.messageType] ?? '';

  return (
    <div className={cn('flex gap-3 mb-4', isOwn ? 'flex-row-reverse' : 'flex-row')}>
      <div className={cn(
        'max-w-[72%] rounded-2xl px-4 py-3 text-sm leading-relaxed',
        typeStyle,
        isOwn
          ? 'bg-brand-600 text-white rounded-tr-sm'
          : 'bg-card border border-border/60 text-foreground rounded-tl-sm'
      )}>
        {!isOwn && msg.senderUser && (
          <div className="mb-1 text-[10px] font-semibold text-muted-foreground">
            {msg.senderUser.firstName} {msg.senderUser.lastName}
          </div>
        )}
        <p className="whitespace-pre-wrap">{msg.body}</p>
        {(msg.attachments?.length ?? 0) > 0 && (
          <div className="mt-2 space-y-1">
            {msg.attachments!.map(a => (
              <div key={a.id} className={cn(
                'flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs',
                isOwn ? 'bg-white/15' : 'bg-muted/40'
              )}>
                <Paperclip size={10} />
                <span className="truncate">{a.document?.name ?? 'Attachment'}</span>
              </div>
            ))}
          </div>
        )}
        <div className={cn('mt-1.5 text-[9.5px] text-right', isOwn ? 'text-brand-200' : 'text-muted-foreground/50')}>
          {timeLabel(msg.createdAt)}
        </div>
      </div>
    </div>
  );
}

// ─── Context panel ────────────────────────────────────────────

function ContextPanel({ thread }: { thread: MessageThread }) {
  const navigate = useNavigate();
  const matters  = useMattersStore(s => s.matters);
  const hearings = useHearingsStore(s => s.hearings);

  const matter = thread.matterId ? matters.find(m => m.id === thread.matterId) : null;
  const nextHearing = matter
    ? hearings
        .filter(h => h.matterId === matter.id && new Date(h.date) > new Date() && ['scheduled','confirmed'].includes(h.status))
        .sort((a,b) => a.date.localeCompare(b.date))[0]
    : null;
  const daysToHearing = nextHearing
    ? Math.ceil((new Date(nextHearing.date).getTime() - Date.now()) / 86_400_000)
    : null;

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="px-4 pt-4 pb-2 border-b border-border/40">
        <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground/50 mb-2">Thread context</p>
      </div>

      <div className="flex-1 px-4 py-3 space-y-5 text-xs">

        {/* Linked matter */}
        {matter ? (
          <div>
            <p className="text-[9.5px] font-bold uppercase tracking-[0.15em] text-muted-foreground/50 mb-2">Matter</p>
            <button onClick={() => navigate(`/barrister/matters/${matter.id}`)}
              className="w-full text-left rounded-xl border border-border/50 px-3 py-2.5 hover:bg-muted/30 transition group">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate font-semibold text-foreground text-xs">{matter.name}</div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground">{matter.number}</div>
                </div>
                <CaretRight size={11} className="shrink-0 mt-0.5 text-muted-foreground opacity-0 group-hover:opacity-60 transition" />
              </div>
              <div className="mt-1.5 flex items-center gap-1.5">
                <span className="rounded-md bg-muted px-1.5 py-0.5 text-[9.5px] capitalize text-muted-foreground">
                  {matter.status?.replace(/_/g,' ')}
                </span>
                {matter.area && <span className="text-[10px] text-muted-foreground">{matter.area}</span>}
              </div>
            </button>
          </div>
        ) : (
          <div>
            <p className="text-[9.5px] font-bold uppercase tracking-[0.15em] text-muted-foreground/50 mb-2">Matter</p>
            <p className="text-xs text-muted-foreground/50 italic">No matter linked</p>
          </div>
        )}

        {/* Next hearing */}
        {nextHearing && (
          <div>
            <p className="text-[9.5px] font-bold uppercase tracking-[0.15em] text-muted-foreground/50 mb-2">Next Hearing</p>
            <div className={cn(
              'rounded-xl border px-3 py-2.5',
              daysToHearing! <= 7 ? 'border-red-200 dark:border-red-500/30 bg-red-50/40 dark:bg-red-500/10' : 'border-amber-200 dark:border-amber-500/30 bg-amber-50/30 dark:bg-amber-500/10'
            )}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs font-semibold text-foreground">{nextHearing.type}</div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground">
                    {new Date(nextHearing.date).toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short' })}
                    {nextHearing.time && ` · ${nextHearing.time}`}
                  </div>
                  {nextHearing.court && <div className="text-[10px] text-muted-foreground">{nextHearing.court}</div>}
                </div>
                <span className={cn(
                  'shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold',
                  daysToHearing! <= 7 ? 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300' : 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300'
                )}>{daysToHearing}d</span>
              </div>
            </div>
          </div>
        )}

        {/* Participants */}
        {thread.participants && thread.participants.length > 0 && (
          <div>
            <p className="text-[9.5px] font-bold uppercase tracking-[0.15em] text-muted-foreground/50 mb-2">Participants</p>
            <div className="space-y-1.5">
              {thread.participants.map(p => (
                <div key={p.userId} className="flex items-center gap-2 text-xs">
                  <div className="h-6 w-6 shrink-0 rounded-full bg-muted flex items-center justify-center text-[9px] font-semibold text-muted-foreground">
                    {(p.user?.firstName?.[0] ?? '?')}{(p.user?.lastName?.[0] ?? '')}
                  </div>
                  <span className="truncate text-foreground/80">{p.user?.firstName} {p.user?.lastName}</span>
                  {p.role === 'owner' && <span className="text-[9px] text-muted-foreground/50">Owner</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick actions */}
        {matter && (
          <div>
            <p className="text-[9.5px] font-bold uppercase tracking-[0.15em] text-muted-foreground/50 mb-2">Quick Actions</p>
            <div className="space-y-1">
              {[
                { label: 'View matter', fn: () => navigate(`/barrister/matters/${matter.id}`) },
                { label: 'Generate fee note', fn: () => navigate(`/barrister/matters/${matter.id}?tab=fees`) },
                { label: 'Schedule hearing', fn: () => navigate(`/barrister/hearings`) },
                { label: 'Upload document', fn: () => navigate(`/barrister/documents`) },
              ].map(a => (
                <button key={a.label} onClick={a.fn}
                  className="flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-xs text-muted-foreground hover:bg-muted/40 hover:text-foreground transition">
                  {a.label} <CaretRight size={10} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ──════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════

type Folder = 'all' | 'matters' | 'external' | 'internal' | 'flagged';

export default function MyCommunicationsPage() {
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const qc = useQueryClient();
  const fetchMatters  = useMattersStore(s => s.fetchMatters);
  const fetchHearings = useHearingsStore(s => s.fetchHearings);

  const [folder, setFolder] = useState<Folder>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showNewThread, setShowNewThread] = useState(false);
  const [showCrossModal, setShowCrossModal] = useState(false);
  const [showContext, setShowContext] = useState(true);
  const [search, setSearch] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [showAttachPicker, setShowAttachPicker] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState<{ id: string; name: string }[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => { fetchMatters(); fetchHearings(); }, [fetchMatters, fetchHearings]);

  // Threads
  const { data: threads = [], isLoading: threadsLoading } = useQuery({
    queryKey: ['messaging', 'threads', folder],
    queryFn: () => {
      const kindMap: Record<Folder, ThreadKind | undefined> = {
        all:      undefined,
        matters:  'matter_internal',
        external: 'matter_external',
        internal: 'chambers_internal',
        flagged:  undefined,
      };
      return fetchThreads({ kind: kindMap[folder], limit: 100 });
    },
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  // Active thread detail
  // Server-side thread search finds threads beyond the 100-row window; the main
  // threads query still drives the unread counts. Canonical hook (H3).
  const { isSearching, results: searchThreads } = useServerSearch(search, {
    key: ['messaging', 'threads-search', folder],
    fetcher: (q) => {
      const kindMap: Record<Folder, ThreadKind | undefined> = {
        all: undefined, matters: 'matter_internal', external: 'matter_external',
        internal: 'chambers_internal', flagged: undefined,
      };
      return fetchThreads({ kind: kindMap[folder], search: q, limit: 50 });
    },
  });

  const { data: activeThread } = useQuery({
    queryKey: ['messaging', 'thread', selectedId],
    queryFn: () => fetchThread(selectedId!),
    enabled: !!selectedId,
    staleTime: 0,
  });

  // Messages
  const { data: messages = [] } = useQuery({
    queryKey: ['messaging', 'messages', selectedId],
    queryFn: () => fetchMessages(selectedId!, { limit: 100 }),
    enabled: !!selectedId,
    staleTime: 0,
    refetchInterval: 15_000,
  });

  // Documents available to attach — scoped to the thread's matter when present, else accessible docs.
  const { data: availableDocs = [] } = useQuery({
    queryKey: ['documents', 'for-attach', activeThread?.matterId ?? null],
    queryFn: () => fetchDocuments(activeThread?.matterId ?? undefined),
    enabled: showAttachPicker,
    staleTime: 30_000,
  });

  // Reset the composer's attachments when switching threads.
  useEffect(() => { setSelectedDocs([]); setShowAttachPicker(false); }, [selectedId]);

  useEffect(() => {
    if (selectedId) {
      const last = messages[messages.length - 1];
      if (last) markThreadRead(selectedId, last.sequence).catch(() => {});
    }
  }, [selectedId, messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Filtered threads
  const displayed = useMemo(() => {
    if (isSearching) return searchThreads; // server already matched (subject + matter)
    let list = threads;
    if (search.trim().length >= 2) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        t.subject?.toLowerCase().includes(q) ||
        t.case?.caseName?.toLowerCase().includes(q) ||
        t.messages?.[0]?.body?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [threads, searchThreads, isSearching, search]);

  // Unread counts
  const totalUnread   = threads.reduce((s, t) => s + (t.unreadCount ?? 0), 0);
  const matterUnread  = threads.filter(t => t.kind === 'matter_internal'  && (t.unreadCount ?? 0) > 0).length;
  const externalUnread = threads.filter(t => t.kind === 'matter_external' && (t.unreadCount ?? 0) > 0).length;

  const handleReply = async () => {
    const text = replyText.trim();
    const documentIds = selectedDocs.map(d => d.id);
    if (!selectedId || replying || (!text && documentIds.length === 0)) return;
    setReplying(true);
    try {
      await sendMessage(
        selectedId,
        text || `Attached ${documentIds.length} document${documentIds.length === 1 ? '' : 's'}`,
        documentIds.length > 0 ? { documentIds } : undefined,
      );
      setReplyText('');
      setSelectedDocs([]);
      setShowAttachPicker(false);
      qc.invalidateQueries({ queryKey: ['messaging', 'messages', selectedId] });
      qc.invalidateQueries({ queryKey: ['messaging', 'threads', folder] });
    } catch { toast.error('Failed to send'); }
    finally { setReplying(false); }
  };

  const toggleDoc = (doc: { id: string; name: string }) =>
    setSelectedDocs(prev => prev.some(d => d.id === doc.id) ? prev.filter(d => d.id !== doc.id) : [...prev, doc]);

  const NAV_ITEMS: { key: Folder; label: string; icon: React.ReactNode; count?: number }[] = [
    { key: 'all',      label: 'All messages',  icon: <ChatTeardropDots size={14} />, count: totalUnread || undefined },
    { key: 'matters',  label: 'By matter',     icon: <Briefcase size={14} />,        count: matterUnread || undefined },
    { key: 'external', label: 'External',      icon: <ArrowSquareOut size={14} />,   count: externalUnread || undefined },
    { key: 'internal', label: 'Internal',      icon: <Buildings size={14} /> },
  ];

  return (
    <>
      {/* Every page needs one top-level heading: it is what a screen reader announces on
          arrival and what heading navigation jumps between. This screen's design has no
          single visible title, so the heading is for assistive technology only and the
          layout is untouched. */}
      <h1 className="sr-only">Communications</h1>
    <div className="mb-3">
      <BackButton onClick={() => navigate(-1)} />
    </div>
    <CommunicationsTabBar />
    <div className="flex h-[calc(100vh-92px)] flex-col overflow-hidden rounded-xl border border-border/60 bg-card">

      {/* ── COMPACT BAR (below xl, where the 220px nav column does not fit) ──
          Three fixed panes need ~900px. Under that the left nav is folded into this
          bar so the thread list and the conversation keep their room. */}
      <div className="flex shrink-0 flex-col gap-2 border-b border-border/40 bg-muted/10 p-2.5 xl:hidden">
        <div className="flex items-center gap-2">
          <button onClick={() => setShowNewThread(true)}
            className="flex shrink-0 items-center gap-1.5 rounded-xl bg-brand-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-brand-700">
            <PencilSimple size={13} weight="bold" />New message
          </button>
          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-border/60 bg-background px-3 py-2">
            <MagnifyingGlass size={12} className="shrink-0 text-muted-foreground/50" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search..." className="min-w-0 flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground/40 focus:outline-none" />
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {NAV_ITEMS.map(item => (
            <button key={item.key} onClick={() => { setFolder(item.key); setSelectedId(null); }}
              className={cn(
                'flex flex-1 items-center justify-center gap-1 rounded-lg px-1.5 py-1.5 text-[11px] font-medium transition',
                folder === item.key
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
              )}>
              <span className="truncate">{item.label}</span>
              {item.count && (
                <span className={cn('flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-[9px] font-bold',
                  folder === item.key ? 'bg-white/25 text-white' : 'bg-brand-600 text-white')}>
                  {item.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="flex min-h-0 flex-1">

      {/* ── LEFT NAV (220px, xl and up) ───────────────────────── */}
      <div className="hidden w-[220px] shrink-0 flex-col border-r border-border/40 bg-muted/10 xl:flex">

        {/* Compose buttons */}
        <div className="p-3 space-y-2">
          <button onClick={() => setShowNewThread(true)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-700 transition">
            <PencilSimple size={13} weight="bold" />New message
          </button>
          <button onClick={() => setShowCrossModal(true)}
            title="Start a secure in-app conversation with a barrister in another chambers"
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-2 text-[11px] font-semibold text-muted-foreground hover:border-brand-300 hover:text-foreground transition">
            <GlobeHemisphereWest size={13} />Message another chambers
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-2 space-y-0.5">
          {NAV_ITEMS.map(item => (
            <button key={item.key} onClick={() => { setFolder(item.key); setSelectedId(null); }}
              className={cn(
                'flex w-full items-center justify-between rounded-xl px-3 py-2 text-xs font-medium transition-all',
                folder === item.key
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
              )}>
              <span className="flex items-center gap-2.5">{item.icon}{item.label}</span>
              {item.count && (
                <span className={cn('rounded-full h-4 min-w-4 px-1 text-[9px] font-bold flex items-center justify-center',
                  folder === item.key ? 'bg-white/25 text-white' : 'bg-brand-600 text-white')}>
                  {item.count}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Search */}
        <div className="p-3 border-t border-border/40">
          <div className="flex items-center gap-2 rounded-xl bg-background border border-border/60 px-3 py-2">
            <MagnifyingGlass size={12} className="shrink-0 text-muted-foreground/50" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search..." className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground/40 focus:outline-none" />
          </div>
        </div>
      </div>

      {/* ── THREAD LIST (full width on a phone, 320px from md) ── */}
      <div className={cn('flex-col border-r border-border/40 md:flex md:w-[320px] md:shrink-0',
        selectedId ? 'hidden md:flex' : 'flex w-full')}>
        <div className="flex items-center justify-between border-b border-border/40 px-4 py-3">
          <span className="text-xs font-semibold text-foreground capitalize">
            {folder === 'all' ? 'All messages' : folder === 'matters' ? 'By matter' : folder === 'external' ? 'External' : 'Internal'}
          </span>
          <span className="text-[10px] text-muted-foreground">{displayed.length} thread{displayed.length !== 1 ? 's' : ''}</span>
        </div>

        <div className="flex-1 overflow-y-auto">
          {threadsLoading ? (
            <div className="flex h-32 items-center justify-center">
              <CircleNotch size={18} className="animate-spin text-muted-foreground/30" />
            </div>
          ) : displayed.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
              <ChatTeardropDots size={28} weight="duotone" className="text-muted-foreground/20" />
              <div className="text-xs text-muted-foreground">No messages</div>
            </div>
          ) : (
            displayed.map(t => (
              <ThreadRow key={t.id} thread={t} isActive={selectedId === t.id}
                currentUserId={user?.id}
                onClick={() => setSelectedId(t.id)} />
            ))
          )}
        </div>
      </div>

      {/* ── ACTIVE THREAD (flex-1) ───────────────────────────── */}
      <div className={cn('flex-1 flex-col min-w-0', selectedId ? 'flex' : 'hidden md:flex')}>

        {selectedId && activeThread ? (
          <>
            {/* Thread header */}
            <div className="flex items-center justify-between border-b border-border/40 px-3 py-3 md:px-5">
              <button onClick={() => setSelectedId(null)}
                aria-label="Back to messages"
                className="mr-1 shrink-0 rounded-lg p-1.5 text-muted-foreground transition hover:bg-muted/50 hover:text-foreground md:hidden">
                <CaretLeft size={16} weight="bold" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-foreground truncate">{threadTitle(activeThread, user?.id)}</span>
                  {activeThread.kind === 'cross_chambers' && (
                    <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                      <GlobeHemisphereWest size={10} weight="fill" />External · Different chambers
                    </span>
                  )}
                  {activeThread.privilege !== 'none' && PRIVILEGE_LABELS[activeThread.privilege]?.label && (
                    <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-[8.5px] font-bold',
                      PRIVILEGE_LABELS[activeThread.privilege]?.color ?? '')}>
                      {PRIVILEGE_LABELS[activeThread.privilege]?.label}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                  <span className="opacity-60">{KIND_ICONS[activeThread.kind]}</span>
                  <span>{activeThread.kind === 'cross_chambers' ? 'Cross-chambers' : activeThread.kind.replace(/_/g,' ')}</span>
                  {activeThread.case?.caseNumber && (
                    <><span>·</span><span className="font-medium text-brand-600 dark:text-brand-400">{activeThread.case.caseNumber}</span></>
                  )}
                </div>
              </div>
              <button onClick={() => setShowContext(v => !v)}
                className={cn('ml-3 hidden rounded-lg px-2.5 py-1.5 text-xs font-medium transition lg:block',
                  showContext ? 'bg-brand-50 text-brand-700 dark:text-brand-300' : 'text-muted-foreground hover:bg-muted/50')}>
                <Info size={13} />
              </button>
            </div>

            <div className="flex flex-1 min-h-0">
              {/* Messages */}
              <div className="flex flex-1 flex-col min-w-0">
                <div className="flex-1 overflow-y-auto px-5 py-4">
                  {messages.map(msg => (
                    <MessageBubble key={msg.id} msg={msg} isOwn={msg.senderUserId === user?.id} />
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply box */}
                <div className="border-t border-border/40 p-4">
                  {/* Selected attachment chips */}
                  {selectedDocs.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {selectedDocs.map(d => (
                        <span key={d.id} className="inline-flex items-center gap-1 rounded-full bg-brand-50 border border-brand-200 py-0.5 pl-2 pr-1 text-[11px] font-medium text-brand-700 dark:text-brand-300">
                          <Paperclip size={10} />
                          <span className="max-w-[160px] truncate">{d.name}</span>
                          <button onClick={() => toggleDoc(d)} className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-brand-400 hover:bg-brand-200 hover:text-brand-700 dark:hover:text-brand-300">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-3">
                    {/* Attach documents — hidden for cross-chambers corridors (no attachments in MVP) */}
                    {activeThread.kind !== 'cross_chambers' && (
                    <div className="relative self-end">
                      <button
                        type="button"
                        onClick={() => setShowAttachPicker(s => !s)}
                        title="Attach document"
                        className={cn(
                          'flex h-10 w-10 items-center justify-center rounded-xl border transition',
                          showAttachPicker || selectedDocs.length > 0
                            ? 'border-brand-200 bg-brand-50 text-brand-600 dark:text-brand-400'
                            : 'border-border/60 text-muted-foreground hover:bg-muted'
                        )}>
                        <Paperclip size={15} />
                      </button>
                      {showAttachPicker && (
                        <div className="absolute bottom-full left-0 z-30 mb-2 w-72 overflow-hidden rounded-xl border border-border bg-card shadow-lg">
                          <div className="border-b border-border/60 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Attach documents</div>
                          <div className="max-h-52 overflow-y-auto">
                            {availableDocs.length === 0 ? (
                              <div className="px-3 py-4 text-center text-xs text-muted-foreground">No documents available</div>
                            ) : (
                              availableDocs.map(doc => {
                                const isSel = selectedDocs.some(d => d.id === doc.id);
                                return (
                                  <button key={doc.id} type="button" onClick={() => toggleDoc({ id: doc.id, name: doc.name })}
                                    className={cn('flex w-full items-center gap-2 border-b border-border/40 px-3 py-2 text-left text-xs last:border-b-0 hover:bg-muted/40', isSel && 'bg-brand-50/60')}>
                                    <Paperclip size={11} className={isSel ? 'text-brand-600 dark:text-brand-400' : 'text-muted-foreground'} />
                                    <span className="flex-1 truncate">{doc.name}</span>
                                    {isSel && <span className="text-[10px] font-semibold text-brand-600 dark:text-brand-400">✓</span>}
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    )}
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleReply(); }}
                      placeholder="Reply… (⌘↩ to send)"
                      rows={2}
                      className="flex-1 resize-none rounded-xl border border-border/60 bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                    />
                    <button onClick={handleReply} disabled={(!replyText.trim() && selectedDocs.length === 0) || replying}
                      className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition self-end',
                        (replyText.trim() || selectedDocs.length > 0) ? 'bg-brand-600 text-white hover:bg-brand-700' : 'bg-muted text-muted-foreground cursor-not-allowed'
                      )}>
                      {replying ? <CircleNotch size={15} className="animate-spin" /> : <PaperPlaneTilt size={15} weight="bold" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Context panel */}
              {showContext && (
                <div className="hidden w-[260px] shrink-0 border-l border-border/40 lg:block">
                  <ContextPanel thread={activeThread} />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center px-8">
            <ChatTeardropDots size={36} weight="duotone" className="text-muted-foreground/20" />
            <div className="text-sm font-semibold text-muted-foreground/50">Select a thread to read</div>
            <div className="text-xs text-muted-foreground/40">Or start a new conversation</div>
            <button onClick={() => setShowNewThread(true)}
              className="mt-2 flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-xs font-semibold text-white hover:bg-brand-700 transition">
              <PencilSimple size={13} weight="bold" />New message
            </button>
          </div>
        )}
      </div>

      </div>

      {/* New conversation — internal messaging only (external email lives in Inbox) */}
      {showNewThread && (
        <NewThreadModal
          defaultKind="chambers_internal"
          onClose={() => setShowNewThread(false)}
          onCreated={(thread) => {
            setShowNewThread(false);
            setSelectedId(thread.id);
            qc.invalidateQueries({ queryKey: ['messaging', 'threads', folder] });
          }}
        />
      )}

      {/* Cross-workspace DM — a barrister at another chambers (in-app, isolated corridor) */}
      {showCrossModal && (
        <CrossChambersModal
          onClose={() => setShowCrossModal(false)}
          onCreated={(thread) => {
            setShowCrossModal(false);
            setFolder('all');
            setSelectedId(thread.id);
            qc.invalidateQueries({ queryKey: ['messaging', 'threads', folder] });
          }}
        />
      )}
    </div>
    </>
  );
}
